import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { UserEmailPreferencesValidation } from '@/lib/validations'
import { usePartnerPageDataStore } from '@/store/store'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { profileService } from 'api/api'
import { useAuth } from 'context/JWTContext'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { z } from 'zod'
import { PartnerPageData } from 'types/types'
import { isStarSportsPage } from '@/utils/starSportsUtils'

type UserEmailPreferencesProps = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  page: PartnerPageData
}

export default function UserEmailPreferences({
  isOpen,
  setIsOpen,
  page,
}: UserEmailPreferencesProps) {
  const { user } = useAuth()
  const { data: userPreferences } = useQuery({
    queryKey: [QUERY_KEYS.USER_PREFERENCES, page?.game_id, user?.id],
    queryFn: () => profileService.getUserEmailPreferences(page?.game_id ?? 0),
  })

  const queryClient = useQueryClient()
  const { mutate, isLoading: isMutating } = useMutation({
    mutationFn: (values: z.infer<typeof UserEmailPreferencesValidation>) =>
      profileService.updateUserEmailPreferences(page?.game_id ?? 0, values),
    onSuccess: () => {
      setIsOpen(false)
      queryClient.invalidateQueries([QUERY_KEYS.USER_PREFERENCES, page?.game_id, user?.id])
    },
  })

  const { formState, handleSubmit, control } = useForm<
    z.infer<typeof UserEmailPreferencesValidation>
  >({})

  const handleSubmitForm = (data: z.infer<typeof UserEmailPreferencesValidation>) => {
    mutate(data)
  }

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm">
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <IconButton sx={{ alignSelf: 'flex-end', p: 0 }} onClick={() => setIsOpen(false)}>
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            p: 1,
            color: page.base_game_text_color,
            fontFamily: page.fonts.secondary_font.family,
          }}
          textAlign="center"
        >
          Email Preferences
        </DialogTitle>

        <DialogContentText
          textAlign="center"
          sx={{ color: page.base_game_text_color, fontFamily: page.fonts.secondary_font.family }}
        >
          Select which emails you want to receive.
        </DialogContentText>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          {userPreferences ? (
            <form
              onSubmit={handleSubmit(handleSubmitForm)}
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Controller
                      control={control}
                      name="HOUR_BEFORE_DEADLINE"
                      defaultValue={userPreferences?.HOUR_BEFORE_DEADLINE ?? true}
                      render={({ field }) => (
                        <Checkbox
                          style={{
                            color: isStarSportsPage(page) ? '#13294b' : page.primary_button_color,
                          }}
                          checked={!!field.value}
                          {...field}
                        />
                      )}
                    />
                  }
                  label="One hour before deadline"
                />
                <FormControlLabel
                  control={
                    <Controller
                      control={control}
                      name="ROUND_FINISHED"
                      defaultValue={userPreferences?.ROUND_FINISHED}
                      render={({ field }) => (
                        <Checkbox
                          style={{
                            color: isStarSportsPage(page) ? '#13294b' : page.primary_button_color,
                          }}
                          checked={!!field.value}
                          {...field}
                        />
                      )}
                    />
                  }
                  label="Round finished"
                />
                <FormControlLabel
                  control={
                    <Controller
                      control={control}
                      name="GAME_FINISHED"
                      defaultValue={userPreferences?.GAME_FINISHED ?? true}
                      render={({ field }) => (
                        <Checkbox
                          style={{
                            color: isStarSportsPage(page) ? '#13294b' : page.primary_button_color,
                          }}
                          checked={!!field.value}
                          {...field}
                        />
                      )}
                    />
                  }
                  label="Game finished"
                />
              </Box>
              <Button
                type="submit"
                sx={{ textTransform: 'none', backgroundColor: page.primary_button_color }}
                variant="contained"
                disabled={isMutating || !formState.isDirty}
              >
                Submit
              </Button>
            </form>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </Box>
    </Dialog>
  )
}
